import { useEffect, useState } from 'react'
import { supabase, readableError } from '../lib/supabase'
import { Field, Button, Check } from './ui'

/**
 * Authenticator-app (TOTP) two-factor. One factor at a time by design — this
 * is the free tier of MFA (Supabase's own factor types, no SMS provider
 * bill), not a general multi-factor manager. Enrolling calls mfa.enroll(),
 * which returns a factor sitting in 'unverified' status until a real code
 * from the app proves the secret was actually copied correctly; an abandoned
 * unverified factor is unenrolled on cancel so it can't block a retry.
 */
export default function Mfa() {
  const [factors, setFactors] = useState(null)
  const [enrolling, setEnrolling] = useState(null) // { id, qr, secret }
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const load = async () => {
    const { data, error } = await supabase.auth.mfa.listFactors()
    if (error) return setErr(readableError(error))
    setFactors(data.totp.filter((f) => f.status === 'verified'))
  }

  useEffect(() => { load() }, [])

  const start = async () => {
    setErr(null); setBusy(true)
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp', friendlyName: `Authenticator ${Date.now()}`,
    })
    setBusy(false)
    if (error) return setErr(readableError(error))
    setEnrolling({ id: data.id, qr: data.totp.qr_code, secret: data.totp.secret })
  }

  const cancel = async () => {
    if (enrolling) await supabase.auth.mfa.unenroll({ factorId: enrolling.id })
    setEnrolling(null); setCode(''); setErr(null)
  }

  const confirm = async (e) => {
    e.preventDefault()
    setBusy(true); setErr(null)
    const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId: enrolling.id })
    if (chErr) { setBusy(false); return setErr(readableError(chErr)) }
    const { error } = await supabase.auth.mfa.verify({
      factorId: enrolling.id, challengeId: ch.id, code,
    })
    setBusy(false)
    if (error) return setErr(readableError(error))
    setEnrolling(null); setCode('')
    load()
  }

  const remove = async (factorId) => {
    setBusy(true); setErr(null)
    const { error } = await supabase.auth.mfa.unenroll({ factorId })
    setBusy(false)
    if (error) return setErr(readableError(error))
    load()
  }

  if (!factors) return null

  return (
    <>
      <h3 className="sechead">
        Two-factor authentication
        <span className="note">
          A second step at sign-in, from an authenticator app — Google Authenticator,
          Authy, and 1Password all work.
        </span>
      </h3>

      <div className="rows">
      {factors.map((f) => (
        <div className="methodrow" key={f.id}>
          <span className="mi" aria-hidden="true"><Check size={12} w={3} /></span>
          <span className="mt">
            Authenticator app
            <small>Enabled — a code from your app is asked for at sign-in</small>
          </span>
          <button className="iconbtn" disabled={busy} onClick={() => remove(f.id)}>Remove</button>
        </div>
      ))}

      {/* The empty state is a row, not a full-width slab. It sits where the
          enabled factor would sit and reads as the same kind of thing. */}
      {!factors.length && !enrolling && (
        <button className="action" disabled={busy} onClick={start}>
          <span>{busy ? 'Starting…' : 'Add authenticator app'}
            <small>Not set up — sign-in asks for your password or code only</small></span>
          <span className="ar">›</span>
        </button>
      )}

      {enrolling && (
        <form className="rowbody" onSubmit={confirm}>
          <p className="small">
            Scan this with your authenticator app, or enter the code by hand.
          </p>
          <img src={enrolling.qr} alt="QR code for authenticator app" width={160} height={160}
               style={{ borderRadius: 12, background: '#fff', padding: 8, marginTop: 12 }} />
          <p className="small mono" style={{ margin: '10px 0', wordBreak: 'break-all' }}>
            {enrolling.secret}
          </p>
          <Field label="6-digit code">
            <input className="input" inputMode="numeric" maxLength={6} autoFocus
                   value={code} placeholder="000000"
                   onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
          </Field>
          <div className="rowbody-acts">
            <Button busy={busy} disabled={code.length !== 6}>Confirm</Button>
            <button type="button" className="btn btn-quiet" onClick={cancel}>Cancel</button>
          </div>
        </form>
      )}
      </div>

      {err && <p className="err" style={{ marginTop: -14, marginBottom: 26 }}>{err}</p>}
    </>
  )
}
